import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import type { DatabaseEntities, Maybe } from '@nova/core';
import { documentKeys } from '@satellite/composables/queryKeys';
import documentService from '@satellite/services/document-service';

type DeleteAttachmentVariables = {
  relatedEntity: DatabaseEntities;
  relatedId: string;
  attachmentId: string;
};

export interface UseAttachmentDeleteOptions {
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: MaybeRefOrGetter<DatabaseEntities>;
  /** The entity's id. Nothing is deleted until there is one. */
  relatedId: MaybeRefOrGetter<Maybe<string>>;
  /** Runs once the removal succeeds, before the list is invalidated. */
  onSuccess?: () => void;
  /** Runs when the removal fails. `deleteAttachment` still resolves. */
  onError?: (error: unknown) => void;
}

/**
 * Removes a document from an entity's attachments and invalidates the list it came from, so
 * every page comes back from the API rather than being patched locally. Pairs with
 * `useAttachmentList`.
 */
export function useAttachmentDelete({
  relatedEntity,
  relatedId,
  onSuccess,
  onError
}: UseAttachmentDeleteOptions) {
  const queryClient = useQueryClient();

  const mutation = useMutation<void, unknown, DeleteAttachmentVariables>({
    // Without this TanStack pauses the mutation while offline: no request, promise never
    // settles, confirmation spins forever instead of reporting the failure.
    networkMode: 'always',
    mutationFn: ({ attachmentId }) => documentService.deleteDocument(attachmentId),
    onSuccess: async (_result, variables) => {
      onSuccess?.();

      await queryClient.invalidateQueries({
        queryKey: documentKeys.list(variables.relatedEntity, variables.relatedId)
      });
    },
    onError
  });

  /**
   * Never rejects: failures reach the caller through `onError`.
   *
   * Both the entity and its id are read here rather than on the way back, so a list that moved
   * on while the request was in flight is not the one invalidated.
   */
  async function deleteAttachment(attachmentId: Maybe<string>): Promise<void> {
    const currentEntity = toValue(relatedEntity);
    const currentId = toValue(relatedId);
    if (!currentId || !attachmentId) {
      return;
    }

    try {
      await mutation.mutateAsync({
        relatedEntity: currentEntity,
        relatedId: currentId,
        attachmentId
      });
    } catch {
      // Reported by onError.
    }
  }

  return {
    deleteAttachment,
    isDeleting: computed(() => mutation.isPending.value)
  };
}
